import {
  getObject,
  getLeoUuid,
  LeoUUID,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { FileAttributes, FileAttributesDTO } from "../types/fileAttributes";
/* eslint-disable import/export */
export abstract class GetPresignedUploadURLForClientLogoRPC implements
  LeoRPC<GetPresignedUploadURLForClientLogoRPC.Request, GetPresignedUploadURLForClientLogoRPC.Response, GetPresignedUploadURLForClientLogoRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedUploadURLForClientLogoRPC.Request
  ): Promise<LeoRPCResult<GetPresignedUploadURLForClientLogoRPC.Response, GetPresignedUploadURLForClientLogoRPC.Errors.Errors>>;
}

export namespace GetPresignedUploadURLForClientLogoRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    fileAttributes: FileAttributesDTO;
    surveyId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedUploadURLForClientLogoRPCRequest: boolean = true;
  
    readonly fileAttributes: FileAttributes;
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    fileAttributes: FileAttributes, 
    surveyId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.fileAttributes = fileAttributes;
      this.surveyId = surveyId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const fileAttributes = FileAttributes.fromDTO(getObject(dto, "fileAttributes", InvalidRequestFileAttributesError));
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        fileAttributes,
        surveyId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          fileAttributes: this.fileAttributes.toDTO(),
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedUploadURLForClientLogoRPC(fileAttributes='${this.fileAttributes}',surveyId='${this.surveyId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
    imageURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedUploadURLForClientLogoResponse: boolean = true;
  
    readonly documentURL: URL;
    readonly imageURL: URL;
  
  
    constructor(
    documentURL: URL, 
    imageURL: URL
    ) {
      this.documentURL = documentURL;
      this.imageURL = imageURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      const imageURL = getUrl(dto, "imageURL", InvalidResponseImageUrlError);
      return new Response(
        documentURL,
        imageURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString(),
          imageURL: this.imageURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedUploadURLForClientLogo(documentURL='${this.documentURL}',imageURL='${this.imageURL}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForClientLogoRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForClientLogoRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForClientLogoRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestFileAttributesError extends GetPresignedUploadURLForClientLogoRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFileAttributesError";
  }
}

export class InvalidRequestSurveyIdError extends GetPresignedUploadURLForClientLogoRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends GetPresignedUploadURLForClientLogoRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedUploadURLForClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseImageUrlError extends GetPresignedUploadURLForClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseImageUrlError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetPresignedUploadURLForClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPresignedUploadURLForClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GetPresignedUploadURLForClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

